import React, { forwardRef, useEffect, useImperativeHandle } from "react";
import { sortResumeForDisplay } from "@/Utils/DateSortingUtils";
import { DEFAULT_SECTION_ORDER, getOrderedSections } from "@/Utils/SectionOrderingUtils";

// Helper function to format dates to MM/YYYY format
const formatDateToMMYYYY = (dateStr) => {
  if (!dateStr) return "";
  if (dateStr === "Present" || dateStr === "Current" || dateStr === "present" || dateStr === "current") {
    return "Present";
  }

  try {
    // Try to parse various date formats
    let date;

    // Check if already in MM/YYYY or MM-YYYY format
    if (/^\d{2}[-/]\d{4}$/.test(dateStr)) {
      return dateStr.replace("-", "/"); // Normalize to MM/YYYY
    }

    // Check if it's a year only (YYYY)
    if (/^\d{4}$/.test(dateStr)) {
      return `01/${dateStr}`; // Default to January
    }

    // Try to parse the date
    date = new Date(dateStr);

    if (date && !isNaN(date.getTime())) {
      // Format as MM/YYYY
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${month}/${year}`;
    }

    // If all parsing fails, return original string
    return dateStr;
  } catch (error) {
    console.warn("Date formatting error:", dateStr, error);
    return dateStr;
  }
};

// Printable Resume Component with Enhanced Print CSS
// This component uses optimized print styles for client-side printing without Puppeteer
const PrintableResume = forwardRef(
  (
    {
      resumeData,
      selectedTemplate,
      onPrint,
      showPrintButton = false,
      onDownloadComplete,
      navigate,
      customSectionOrder = null,
      customStyles = {},
    },
    ref
  ) => {
    const { parsedData } = resumeData || {};

    // Sort the resume data by date for consistent ordering
    const sortedData = parsedData ? sortResumeForDisplay(parsedData) : null;

    // Get template-specific defaults
    const getTemplateDefaults = () => {
      const templateId = selectedTemplate?.id || "default";

      switch (templateId) {
        case "modern":
          return {
            fontSize: 14,
            sectionSpacing: 16,
            titleColor: "#2563eb",
            subtitleColor: "#1e40af",
          };
        case "creative":
          return {
            fontSize: 14,
            sectionSpacing: 16,
            titleColor: "#667eea",
            subtitleColor: "#764ba2",
          };
        case "classic":
        default:
          return {
            fontSize: 14,
            sectionSpacing: 16,
            titleColor: "#333333",
            subtitleColor: "#555555",
          };
      }
    };

    // Extract custom styles with template-appropriate defaults
    const defaults = getTemplateDefaults();
    const {
      fontSize = defaults.fontSize,
      sectionSpacing = defaults.sectionSpacing,
      titleColor = defaults.titleColor,
      subtitleColor = defaults.subtitleColor,
    } = customStyles;

    // Load print CSS
    useEffect(() => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.type = "text/css";
      link.href = "/frontend/Assets/print.css";
      document.head.appendChild(link);

      return () => {
        // Cleanup on unmount
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      };
    }, []);

    // Print CSS approach with hidden iframe for silent PDF generation
    const handlePrint = async () => {
      try {
        // Create a hidden iframe for silent printing/PDF generation
        const iframe = document.createElement("iframe");
        iframe.style.position = "absolute";
        iframe.style.top = "-10000px";
        iframe.style.left = "-10000px";
        iframe.style.width = "8.5in";
        iframe.style.height = "11in";
        document.body.appendChild(iframe);

        // Wait for DOM to be fully updated
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Get the resume content
        const resumeContent = document.querySelector(".printable-resume");
        if (!resumeContent) {
          throw new Error("Resume content not found");
        }

        // Calculate content density for dynamic spacing
        const calculateContentDensity = (data) => {
          const totalItems =
            (data?.experience?.length || 0) +
            (data?.education?.length || 0) +
            (data?.projects?.length || 0) +
            (data?.certifications?.length || 0) +
            (data?.skills ? Object.keys(data.skills).length : 0) +
            (data?.languages?.length || 0);

          const totalText = [
            data?.summary || "",
            data?.objective || "",
            ...(data?.experience || []).map((exp) => (exp.description || "").length),
            ...(data?.projects || []).map((proj) => (proj.description || "").length),
          ].join("").length;

          // Determine density based on total items and text length
          if (totalItems >= 15 || totalText > 2000) return "very-high";
          if (totalItems >= 10 || totalText > 1500) return "high";
          if (totalItems >= 6 || totalText > 1000) return "medium";
          return "low";
        };

        const contentDensity = calculateContentDensity(sortedData);

        // Clone the resume content
        const clonedContent = resumeContent.cloneNode(true);

        // Add content density attribute to printable-resume div
        const printableResumeDiv = clonedContent.querySelector(".printable-resume");
        if (printableResumeDiv) {
          printableResumeDiv.setAttribute("data-content-density", contentDensity);
        }

        // Create complete HTML document with print CSS for the iframe
        const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Resume - ${selectedTemplate?.name || "Resume"}</title>
          <style>
            @media print, screen {
              * { box-sizing: border-box; margin: 0; padding: 0; }
              
              .printable-resume {
                font-family: ${
                  selectedTemplate?.id === "classic"
                    ? "Times, serif"
                    : selectedTemplate?.id === "modern"
                    ? "Arial, sans-serif"
                    : "Georgia, serif"
                };
                max-width: 8.5in;
                width: 100%;
                margin: 0 auto;
                padding: 0.5in;
                background: white;
                color: black;
                line-height: 1.2;
                font-size: ${fontSize}pt;
                box-sizing: border-box;
                min-height: 10.5in;
                overflow: visible;
              }

              .header {
                text-align: center;
                margin-bottom: 6pt;
                border-bottom: ${
                  selectedTemplate?.id === "classic"
                    ? "1pt solid #333"
                    : selectedTemplate?.id === "modern"
                    ? "2pt solid #2563eb"
                    : "1pt solid #667eea"
                };
                padding-bottom: 2pt;
              }

              .header h1 {
                font-size: ${selectedTemplate?.id === "modern" ? "16pt" : "14pt"};
                font-weight: bold;
                margin: 0 0 4pt 0;
                color: ${selectedTemplate?.id === "modern" ? "#2563eb" : "black"};
              }

              .header .contact {
                font-size: 9pt;
                color: #666;
                margin: 0;
              }

              .section {
                margin-bottom: ${sectionSpacing * 0.75}pt;
                page-break-inside: avoid;
              }

              .section-title {
                font-size: 10pt;
                font-weight: bold;
                text-transform: uppercase;
                border-bottom: 1pt solid #333;
                padding-bottom: 1pt;
                margin-bottom: 3pt;
                color: ${titleColor};
              }

              .section-content {
                font-size: 9pt;
                line-height: 1.1;
              }

              .experience-item, .education-item {
                margin-bottom: 3pt;
                page-break-inside: avoid;
              }

              .experience-title, .education-title {
                font-size: 9pt;
                font-weight: bold;
                margin: 0 0 1pt 0;
                color: ${subtitleColor};
              }

              .experience-company, .education-school {
                font-size: 9pt;
                color: ${subtitleColor};
                margin: 0 0 1pt 0;
              }

              .experience-date, .education-date {
                font-size: 9pt;
                color: #666;
                margin: 0 0 3pt 0;
              }

              .experience-description {
                font-size: 8pt;
                line-height: 1.1;
                margin: 0;
                white-space: pre-line;
                color: black;
              }

              .education-gpa {
                font-size: 9pt;
                color: #666;
                margin: 1pt 0 0 0;
              }

              .skills-list {
                font-size: 9pt;
                line-height: 1.1;
                margin: 0;
                display: grid !important;
                grid-template-columns: 1fr 1fr 1fr !important;
                gap: 4pt 8pt !important;
                column-gap: 8pt !important;
              }
              
              @media print {
                .skills-list {
                  display: grid !important;
                  grid-template-columns: 1fr 1fr 1fr !important;
                  gap: 2pt 6pt !important;
                  column-gap: 6pt !important;
                  page-break-inside: avoid;
                }
                
                .skill-item {
                  white-space: nowrap !important;
                  overflow: hidden !important;
                  font-size: 8pt !important;
                  line-height: 1.1 !important;
                  page-break-inside: avoid;
                }
              }
              
              .skill-item {
                margin: 0;
                padding: 0;
                display: flex;
                align-items: flex-start;
                word-wrap: break-word;
                font-size: 9pt;
                line-height: 1.2;
                margin-bottom: 2pt;
              }
              
              .skill-item:before {
                content: "•";
                margin-right: 4pt;
                color: black;
                flex-shrink: 0;
              }

              /* Modern template specific styles */
              .modern-header {
                background: #2563eb !important;
                color: white !important;
                margin: -0.5in -0.5in 3pt -0.5in !important;
                padding: 4pt 0.5in !important;
                text-align: center !important;
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
              }

              .modern-header h1 {
                color: white !important;
                font-size: 14pt !important;
                font-weight: bold !important;
                margin: 0 0 2pt 0 !important;
              }

              .modern-header .contact {
                color: rgba(255, 255, 255, 0.9) !important;
                font-size: 9pt !important;
              }

              .modern .section-title {
                color: ${titleColor} !important;
                border-left: 3pt solid ${titleColor} !important;
                border-bottom: none !important;
                padding-left: 6pt !important;
                padding-bottom: 0 !important;
                font-size: 10pt !important;
                font-weight: bold !important;
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
              }

              /* Creative template specific styles */
              .creative-header {
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
                background-color: #667eea !important;
                color: white !important;
                margin: -0.5in -0.5in 3pt -0.5in !important;
                padding: 4pt 0.5in !important;
                text-align: center !important;
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
              }

              .creative-header h1 {
                color: white !important;
                font-size: 14pt !important;
                font-weight: bold !important;
                margin: 0 0 2pt 0 !important;
              }

              .creative-header .contact {
                color: rgba(255, 255, 255, 0.9) !important;
                font-size: 9pt !important;
              }

              .creative .section-title {
                color: ${titleColor} !important;
                border-bottom: 1pt solid ${titleColor} !important;
                font-size: 10pt !important;
                font-weight: bold !important;
                padding-bottom: 1pt !important;
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
              }

              @page {
                margin: 0.5in;
                size: letter;
              }

              html, body {
                height: 100%;
                width: 100%;
                margin: 0;
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          ${clonedContent.outerHTML}
        </body>
        </html>
      `;

        // Write content to iframe
        const iframeDoc = iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(htmlContent);
        iframeDoc.close();

        // Wait for content to load, then trigger print with user confirmation
        setTimeout(() => {
          try {
            // Conservative approach - no navigation to avoid false positives
            let timeoutId;

            // Cleanup function
            const cleanup = () => {
              if (timeoutId) clearTimeout(timeoutId);

              // Clean up iframe
              if (document.body.contains(iframe)) {
                document.body.removeChild(iframe);
              }
            };

            // Simple event handler - just cleanup, no navigation
            const afterPrintHandler = () => {
              // Remove event listener
              iframe.contentWindow.removeEventListener("afterprint", afterPrintHandler);

              // Cleanup after a short delay
              setTimeout(() => {
                cleanup();
              }, 500);
            };

            // Set up single event listener
            iframe.contentWindow.addEventListener("afterprint", afterPrintHandler);

            // Safety timeout - always cleanup
            timeoutId = setTimeout(() => {
              iframe.contentWindow.removeEventListener("afterprint", afterPrintHandler);
              cleanup();
            }, 30000);

            // Focus iframe and trigger print
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          } catch (printErr) {
            console.error("Print error", printErr.message || printErr);
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
            }
            throw printErr;
          }
        }, 1000);
      } catch (error) {
        console.error("Error with print CSS", error.message || error);
      }

      // Call parent print handler if provided
      if (onPrint) {
        onPrint();
      }
    };

    // Expose handlePrint function to parent component
    useImperativeHandle(ref, () => ({
      handlePrint,
    }));

    const printStyles = `
    <style>
      @media print, screen {
        .printable-resume {
          font-family: ${
            selectedTemplate?.id === "classic"
              ? "Times, serif"
              : selectedTemplate?.id === "modern"
              ? "Arial, sans-serif"
              : "Georgia, serif"
          };
          max-width: 8.5in;
          width: 100%;
          margin: 0 auto;
          padding: 0.5in;
          background: white;
          color: black;
          line-height: 1.4;
          font-size: ${fontSize}px;
          box-sizing: border-box;
          min-height: 10.5in;
          overflow: visible;
        }

        .header {
          text-align: center;
          margin-bottom: 6pt;
          border-bottom: ${
            selectedTemplate.id === "classic"
              ? "1pt solid #333"
              : selectedTemplate.id === "modern"
              ? "2pt solid #2563eb"
              : "1pt solid #667eea"
          };
          padding-bottom: 2pt;
        }

        .header h1 {
          font-size: ${fontSize * 1.4}px;
          font-weight: bold;
          margin: 0 0 4pt 0;
          color: ${titleColor};
        }

        .header .contact {
          font-size: ${fontSize * 0.85}px;
          color: #666;
          margin: 0;
        }

        .section {
          margin-bottom: ${sectionSpacing}px;
          page-break-inside: avoid;
        }

        .section-title {
          font-size: ${fontSize * 1.1}px;
          font-weight: bold;
          text-transform: uppercase;
          border-bottom: 1pt solid #333;
          padding-bottom: 1pt;
          margin-bottom: 3pt;
          color: ${titleColor};
        }

        .section-content {
          font-size: ${fontSize * 0.9}px;
          line-height: 1.4;
        }

        .experience-item, .education-item {
          margin-bottom: 3pt;
          page-break-inside: avoid;
        }

        .experience-title, .education-title {
          font-size: ${fontSize * 0.95}px;
          font-weight: bold;
          margin: 0 0 1pt 0;
          color: ${subtitleColor};
        }

        .experience-company, .education-school {
          font-size: ${fontSize * 0.85}px;
          color: ${subtitleColor};
          margin: 0 0 1pt 0;
        }

        .experience-date, .education-date {
          font-size: ${fontSize * 0.85}px;
          color: #666;
          margin: 0 0 3pt 0;
        }

        .experience-description {
          font-size: ${fontSize * 0.8}px;
          line-height: 1.1;
          margin: 0;
          white-space: pre-line;
          color: black;
        }

        .education-gpa {
          font-size: 9pt;
          color: #666;
          margin: 1pt 0 0 0;
        }

        .skills-list {
          font-size: ${fontSize * 0.9}px;
          line-height: 1.1;
          margin: 0;
          display: grid !important;
          grid-template-columns: 1fr 1fr 1fr !important;
          gap: 2px 8px !important;
          column-gap: 8px !important;
        }
        
        .skill-item {
          margin: 0;
          padding: 0;
          display: flex;
          align-items: flex-start;
          word-wrap: break-word;
          font-size: ${fontSize * 0.9}px;
          line-height: 1.2;
          margin-bottom: 2px;
        }
        
        .skill-item:before {
          content: "•";
          margin-right: 4px;
          color: black;
          flex-shrink: 0;
        }

        /* Modern template specific styles */
        .modern-header {
          background: ${titleColor} !important;
          background-color: ${titleColor} !important;
          color: white !important;
          margin: -0.5in -0.5in 20px -0.5in !important;
          padding: 6pt 0.5in 14px 0.5in !important;
          text-align: center !important;
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
          border-radius: 0 0 8px 8px !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
        }

        .modern-header h1 {
          color: white !important;
          font-size: 20pt !important;
          font-weight: bold !important;
          margin: 0 0 8px 0 !important;
          letter-spacing: 0.5px !important;
        }

        .modern-header .contact {
          color: rgba(255, 255, 255, 0.95) !important;
          font-size: 9pt !important;
          line-height: 1.4 !important;
          font-weight: 300 !important;
        }

        /* Modern template section styling */
        .printable-resume.modern .section {
          margin-bottom: ${sectionSpacing}px !important;
        }

        .printable-resume.modern .section-title {
          color: ${titleColor} !important;
          border-left: 4pt solid ${titleColor} !important;
          border-bottom: none !important;
          padding-left: 8pt !important;
          padding-bottom: 0 !important;
          font-size: ${fontSize * 1.1}px !important;
          font-weight: bold !important;
          margin: 0 0 8pt 0 !important;
          text-transform: uppercase !important;
          letter-spacing: 1px !important;
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
        }

        .printable-resume.modern .section-content {
          font-size: ${fontSize * 0.9}px !important;
          line-height: 1.1 !important;
        }

        .printable-resume.modern .experience-item,
        .printable-resume.modern .education-item {
          margin-bottom: ${sectionSpacing * 0.75}px !important;
          padding-left: 4pt !important;
        }

        .printable-resume.modern .experience-title,
        .printable-resume.modern .education-title {
          font-size: ${fontSize * 0.95}px !important;
          font-weight: bold !important;
          color: ${subtitleColor} !important;
          margin: 0 !important;
        }

        .printable-resume.modern .experience-company,
        .printable-resume.modern .education-school {
          font-size: ${fontSize * 0.85}px !important;
          color: ${subtitleColor} !important;
          margin: 2pt 0 6pt 0 !important;
          font-weight: 500 !important;
        }

        .printable-resume.modern .experience-date,
        .printable-resume.modern .education-date {
          font-size: ${fontSize * 0.75}px !important;
          color: #6b7280 !important;
          font-style: italic !important;
          margin: 0 !important;
          background: #f3f4f6 !important;
          padding: 2pt 6pt !important;
          border-radius: 4pt !important;
        }

        .printable-resume.modern .experience-description {
          font-size: ${fontSize * 0.8}px !important;
          line-height: 1.1 !important;
          text-align: justify !important;
          white-space: pre-line !important;
          margin: 4pt 0 0 0 !important;
          color: #374151 !important;
        }

        .printable-resume.modern .skills-list {
          font-size: ${fontSize * 0.9}px !important;
          line-height: 1.1 !important;
          color: #374151 !important;
          display: grid !important;
          grid-template-columns: 1fr 1fr 1fr !important;
          gap: 2px 8px !important;
          column-gap: 8px !important;
        }

        /* Classic template specific styles */
        .classic-header {
          text-align: center !important;
          margin: -0.5in -0.5in 20px -0.5in !important;
          padding: 4pt 0.5in 12px 0.5in !important;
          border-bottom: 2px solid ${titleColor} !important;
        }

        .classic-header h1 {
          font-size: 18pt !important;
          font-weight: bold !important;
          margin: 0 0 8px 0 !important;
          color: ${titleColor} !important;
        }

        .classic-header .contact {
          font-size: 8.5pt !important;
          color: #666 !important;
          margin: 0 !important;
          line-height: 1.2 !important;
        }

        /* Classic template section styling to match chat interface */
        .printable-resume.classic .section {
          margin-bottom: ${sectionSpacing}px !important;
        }

        .printable-resume.classic .section-title {
          font-size: ${fontSize * 1.1}px !important;
          font-weight: bold !important;
          color: ${titleColor} !important;
          margin: 0 0 6pt 0 !important;
          border-bottom: 1px solid ${titleColor} !important;
          padding-bottom: 2pt !important;
          text-transform: uppercase !important;
        }

        .printable-resume.classic .section-content {
          font-size: ${fontSize * 0.9}px !important;
          line-height: 1.1 !important;
        }

        .printable-resume.classic .experience-item,
        .printable-resume.classic .education-item {
          margin-bottom: ${sectionSpacing * 0.75}px !important;
        }

        .printable-resume.classic .experience-title,
        .printable-resume.classic .education-title {
          font-size: ${fontSize * 0.95}px !important;
          font-weight: bold !important;
          color: ${subtitleColor} !important;
          margin: 0 !important;
        }

        .printable-resume.classic .experience-company,
        .printable-resume.classic .education-school {
          font-size: ${fontSize * 0.85}px !important;
          color: ${subtitleColor} !important;
          margin: 0 0 4pt 0 !important;
          font-style: italic !important;
        }

        .printable-resume.classic .experience-date,
        .printable-resume.classic .education-date {
          font-size: ${fontSize * 0.75}px !important;
          color: #666 !important;
          font-style: italic !important;
          margin: 0 !important;
        }

        .printable-resume.classic .experience-description {
          font-size: ${fontSize * 0.8}px !important;
          line-height: 1.1 !important;
          text-align: justify !important;
          white-space: pre-line !important;
          margin: 0 !important;
        }

        .printable-resume.classic .skills-list {
          font-size: ${fontSize * 0.9}px !important;
          line-height: 1.1 !important;
          display: grid !important;
          grid-template-columns: 1fr 1fr 1fr !important;
          gap: 2px 8px !important;
          column-gap: 8px !important;
        }

        /* Creative template specific styles */
        .creative-header {
          background: linear-gradient(135deg, ${titleColor} 0%, ${subtitleColor} 100%) !important;
          background-color: ${titleColor} !important; /* Fallback */
          color: white !important;
          margin: -0.5in -0.5in 20px -0.5in !important;
          padding: 8pt 0.5in 16px 0.5in !important;
          text-align: center !important;
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
          position: relative !important;
          overflow: hidden !important;
        }

        .creative-header::before {
          content: '' !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.1) 100%) !important;
        }

        .creative-header h1 {
          color: white !important;
          font-size: 22pt !important;
          font-weight: bold !important;
          margin: 0 0 10px 0 !important;
          letter-spacing: 1px !important;
          text-shadow: 0 2px 4px rgba(0,0,0,0.3) !important;
          position: relative !important;
          z-index: 1 !important;
        }

        .creative-header .contact {
          color: rgba(255, 255, 255, 0.95) !important;
          font-size: 9.5pt !important;
          line-height: 1.4 !important;
          font-weight: 300 !important;
          position: relative !important;
          z-index: 1 !important;
        }

        /* Creative template section styling */
        .printable-resume.creative .section {
          margin-bottom: ${sectionSpacing}px !important;
          position: relative !important;
        }

        .printable-resume.creative .section-title {
          color: ${titleColor} !important;
          border-bottom: 2pt solid transparent !important;
          background: linear-gradient(90deg, ${titleColor}, ${subtitleColor}) !important;
          background-size: 100% 2pt !important;
          background-repeat: no-repeat !important;
          background-position: bottom !important;
          font-size: ${fontSize * 1.1}px !important;
          font-weight: bold !important;
          padding-bottom: 4pt !important;
          margin: 0 0 10pt 0 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.8px !important;
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
        }

        .printable-resume.creative .section-content {
          font-size: ${fontSize * 0.9}px !important;
          line-height: 1.1 !important;
          position: relative !important;
        }

        .printable-resume.creative .experience-item,
        .printable-resume.creative .education-item {
          margin-bottom: ${sectionSpacing * 0.75}px !important;
          padding: 8pt !important;
          background: rgba(102, 126, 234, 0.02) !important;
          border-left: 3pt solid rgba(102, 126, 234, 0.3) !important;
          border-radius: 0 4pt 4pt 0 !important;
        }

        .printable-resume.creative .experience-title,
        .printable-resume.creative .education-title {
          font-size: ${fontSize * 0.95}px !important;
          font-weight: bold !important;
          color: ${subtitleColor} !important;
          margin: 0 0 4pt 0 !important;
          text-transform: capitalize !important;
        }

        .printable-resume.creative .experience-company,
        .printable-resume.creative .education-school {
          font-size: ${fontSize * 0.85}px !important;
          color: ${subtitleColor} !important;
          margin: 0 0 6pt 0 !important;
          font-weight: 500 !important;
          font-style: italic !important;
        }

        .printable-resume.creative .experience-date,
        .printable-resume.creative .education-date {
          font-size: ${fontSize * 0.75}px !important;
          color: #7c3aed !important;
          font-style: italic !important;
          margin: 0 !important;
          background: linear-gradient(135deg, ${titleColor}, ${subtitleColor}) !important;
          color: white !important;
          padding: 2pt 8pt !important;
          border-radius: 12pt !important;
          display: inline-block !important;
          font-weight: 500 !important;
        }

        .printable-resume.creative .experience-description {
          font-size: ${fontSize * 0.8}px !important;
          line-height: 1.1 !important;
          text-align: justify !important;
          white-space: pre-line !important;
          margin: 6pt 0 0 0 !important;
          color: #374151 !important;
        }

        .printable-resume.creative .skills-list {
          font-size: ${fontSize * 0.9}px !important;
          line-height: 1.1 !important;
          color: #374151 !important;
          display: grid !important;
          grid-template-columns: 1fr 1fr 1fr !important;
          gap: 2px 8px !important;
          column-gap: 8px !important;
        }

        /* Ensure proper page breaks */
        @page {
          margin: 0.5in;
          size: letter;
        }

        /* Allow multi-page layout for complete content */
        html, body {
          height: auto;
          overflow: visible;
        }

        .section {
          page-break-inside: avoid;
          break-inside: avoid;
        }
        
        .experience-item, .education-item {
          page-break-inside: avoid;
          break-inside: avoid;
        }

        .section:last-child {
          margin-bottom: 0;
        }

        .experience-item:last-child,
        .education-item:last-child {
          margin-bottom: 0;
        }

        * {
          box-sizing: border-box;
        }
      }
    </style>
  `;

    // Individual section renderers for flexible ordering
    const renderPersonalInfoSection = (data, templateId) => {
      if (!data) return null;

      const headerClass =
        templateId === "modern"
          ? "modern-header"
          : templateId === "creative"
          ? "creative-header"
          : "classic-header";

      // Get template-specific styles
      const getHeaderStyles = () => {
        if (templateId === "modern") {
          return {
            backgroundColor: "#2563eb",
            color: "white",
            margin: "0 -0.5in 20pt -0.5in",
            padding: "16pt 0.5in",
            textAlign: "center",
            WebkitPrintColorAdjust: "exact",
            colorAdjust: "exact",
          };
        } else if (templateId === "creative") {
          return {
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            backgroundColor: "#667eea", // fallback
            color: "white",
            margin: "0 -0.5in 12pt -0.5in",
            padding: "16pt 0.5in",
            textAlign: "center",
            WebkitPrintColorAdjust: "exact",
            colorAdjust: "exact",
          };
        }
        return {}; // classic template
      };

      const getNameStyles = () => {
        if (templateId === "modern") {
          return {
            color: "white",
            fontSize: "18pt",
            fontWeight: "bold",
            margin: "0 0 6pt 0",
          };
        } else if (templateId === "creative") {
          return {
            color: "white",
            fontSize: "16pt",
            fontWeight: "bold",
            margin: "0 0 6pt 0",
          };
        }
        return {}; // classic template
      };

      const getContactStyles = () => {
        if (templateId === "modern" || templateId === "creative") {
          return {
            color: "rgba(255, 255, 255, 0.9)",
            fontSize: "10pt",
            margin: "0",
          };
        }
        return {}; // classic template
      };

      return (
        <div className={headerClass} style={getHeaderStyles()}>
          <h1 style={getNameStyles()}>{data.name || "Your Name"}</h1>
          <div className="contact" style={getContactStyles()}>
            {[data.email, data.phone, data.location, data.linkedIn, data.github].filter(Boolean).join(" | ")}
          </div>
        </div>
      );
    };

    const renderSummarySection = (data) => {
      // Handle both old string format and new object format
      const summaryText = typeof data === "string" ? data : data?.content || "";
      if (!summaryText || !summaryText.trim()) return null;

      return (
        <div className="section">
          <div className="section-title">Professional Summary</div>
          <div className="section-content">{summaryText}</div>
        </div>
      );
    };

    const renderExperienceSection = (data) => {
      if (!data || !Array.isArray(data) || data.length === 0) return null;

      return (
        <div className="section">
          <div className="section-title">Work Experience</div>
          <div className="section-content">
            {data.map((exp, index) => (
              <div key={index} className="experience-item">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    marginBottom: "2pt",
                  }}
                >
                  <div className="experience-title">{exp.title || "Job Title"}</div>
                  <div className="experience-date">
                    {formatDateToMMYYYY(exp.startDate) || "Start"} - {formatDateToMMYYYY(exp.endDate) || "Present"}
                  </div>
                </div>
                <div className="experience-company">
                  {exp.company || "Company Name"}
                  {exp.location && `, ${exp.location}`}
                </div>
                {exp.description && (
                  <div className="experience-description" style={{ whiteSpace: "pre-line" }}>
                    {exp.description}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    };

    const renderEducationSection = (data) => {
      if (!data || !Array.isArray(data) || data.length === 0) return null;

      return (
        <div className="section">
          <div className="section-title">Education</div>
          <div className="section-content">
            {data.map((edu, index) => (
              <div key={index} className="education-item">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    marginBottom: "2pt",
                  }}
                >
                  <div className="education-title">{edu.degree || "Degree"}</div>
                  <div className="education-date">
                    {formatDateToMMYYYY(edu.startDate) || "Start"} - {formatDateToMMYYYY(edu.endDate) || "End"}
                  </div>
                </div>
                <div className="education-school">
                  {edu.school || "School Name"}
                  {edu.location && `, ${edu.location}`}
                  {edu.gpa && <span style={{ marginLeft: "8pt" }}>GPA: {edu.gpa}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    };

    // Helper function to extract skills array from any format
    const getSkillsArray = (skillsData) => {
      if (Array.isArray(skillsData)) {
        return skillsData;
      } else if (skillsData?.list) {
        return skillsData.list;
      } else if (skillsData?.categorized) {
        return [
          ...(skillsData.categorized.technical || []),
          ...(skillsData.categorized.soft || []),
          ...(skillsData.categorized.tools || []),
          ...(skillsData.categorized.other || []),
        ];
      }
      return [];
    };

    const renderSkillsSection = (data) => {
      const skillsList = getSkillsArray(data);
      if (!skillsList || skillsList.length === 0) return null;

      return (
        <div className="section">
          <div className="section-title">Skills</div>
          <div className="section-content">
            <div className="skills-list">
              {skillsList.map((skill, index) => (
                <div key={index} className="skill-item">
                  {skill}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    };

    const renderProjectsSection = (data) => {
      if (!data || !Array.isArray(data) || data.length === 0) return null;

      return (
        <div className="section">
          <div className="section-title">Projects</div>
          <div className="section-content">
            {data.map((project, index) => (
              <div key={index} className="experience-item">
                <div className="experience-title">
                  {project.name || project.title || "Project Name"}
                  {project.link && (
                    <span style={{ fontSize: "7pt", color: "#0366d6", marginLeft: "8pt" }}>{project.link}</span>
                  )}
                </div>
                {project.description && (
                  <div className="experience-description" style={{ whiteSpace: "pre-line" }}>
                    {project.description}
                  </div>
                )}
                {project.technologies && project.technologies.length > 0 && (
                  <div style={{ fontSize: "7pt", color: "#666", fontStyle: "italic", marginTop: "4pt" }}>
                    Technologies: {project.technologies.join(", ")}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    };

    const renderCertificationsSection = (data) => {
      if (!data || !Array.isArray(data) || data.length === 0) return null;

      return (
        <div className="section">
          <div className="section-title">Certifications</div>
          <div className="section-content">
            {data.map((cert, index) => (
              <div key={index} className="experience-item">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <div className="experience-title">{cert.name || "Certification Name"}</div>
                  <div className="experience-date">{formatDateToMMYYYY(cert.date) || "Date"}</div>
                </div>
                {cert.issuer && <div className="experience-company">{cert.issuer}</div>}
              </div>
            ))}
          </div>
        </div>
      );
    };

    const renderLanguagesSection = (data) => {
      if (!data || !Array.isArray(data) || data.length === 0) return null;

      return (
        <div className="section">
          <div className="section-title">Languages</div>
          <div className="section-content">
            {data.map((lang, index) => (
              <div key={index} className="experience-item">
                <div className="experience-title">{lang.language}</div>
                <div className="experience-company">{lang.proficiency}</div>
              </div>
            ))}
          </div>
        </div>
      );
    };

    // Section renderer mapping
    const sectionRenderers = {
      personalInfo: (data) => renderPersonalInfoSection(data, selectedTemplate?.id),
      summary: renderSummarySection,
      experience: renderExperienceSection,
      education: renderEducationSection,
      skills: renderSkillsSection,
      projects: renderProjectsSection,
      certifications: renderCertificationsSection,
      languages: renderLanguagesSection,
    };

    // Flexible template renderer that respects section ordering
    const renderFlexibleTemplate = (templateClass) => {
      const orderedSections = getOrderedSections(sortedData, customSectionOrder || DEFAULT_SECTION_ORDER);

      // Apply custom styles as CSS custom properties and inline styles
      const customStylesObj = {
        fontSize: `${fontSize}px`,
        lineHeight: "1.4",
        "--section-spacing": `${sectionSpacing}px`,
        "--title-color": titleColor,
        "--subtitle-color": subtitleColor,
        "--font-size": `${fontSize}px`,
        "--section-margin": `${sectionSpacing}px`,
      };

      return (
        <div className={`printable-resume ${templateClass}`} style={customStylesObj}>
          {orderedSections.map((section) => {
            const renderer = sectionRenderers[section.id];
            if (renderer && section.data) {
              return <React.Fragment key={section.id}>{renderer(section.data)}</React.Fragment>;
            }
            return null;
          })}
        </div>
      );
    };

    const renderClassicTemplate = () => (
      <div
        className="printable-resume classic"
        style={{
          fontFamily: "Times, serif",
          backgroundColor: "white",
          padding: "0.4in 0.5in",
          fontSize: "10pt",
          lineHeight: "1.3",
          color: "black",
          margin: "0",
          marginTop: "0",
        }}
      >
        <div
          style={{
            textAlign: "center",
            marginBottom: "8pt",
            marginTop: "0",
            borderBottom: "1pt solid #333",
            paddingBottom: "4pt",
            paddingTop: "0",
          }}
        >
          <div
            style={{
              fontSize: "16pt",
              fontWeight: "bold",
              margin: "0 0 3pt 0",
              color: "black",
              lineHeight: "1.2",
            }}
          >
            {sortedData?.personalInfo?.name || "Your Name"}
          </div>
          <div
            style={{
              fontSize: "9pt",
              color: "#666",
              margin: "0",
              lineHeight: "1.2",
            }}
          >
            {[
              sortedData?.personalInfo?.email,
              sortedData?.personalInfo?.phone,
              sortedData?.personalInfo?.location,
              sortedData?.personalInfo?.linkedIn,
              sortedData?.personalInfo?.github,
            ]
              .filter(Boolean)
              .join(" | ")}
          </div>
        </div>

        {sortedData?.summary && (
          <div style={{ marginBottom: "8pt" }}>
            <div
              style={{
                fontSize: "10pt",
                fontWeight: "bold",
                textTransform: "uppercase",
                borderBottom: "1pt solid #333",
                marginBottom: "3pt",
                color: "black",
                paddingBottom: "1pt",
              }}
            >
              PROFESSIONAL SUMMARY
            </div>
            <div
              style={{
                fontSize: "9pt",
                color: "black",
                lineHeight: "1.3",
                textAlign: "justify",
              }}
            >
              {typeof sortedData.summary === "string" ? sortedData.summary : sortedData.summary?.content || ""}
            </div>
          </div>
        )}

        {sortedData?.experience && sortedData.experience.length > 0 && (
          <div style={{ marginBottom: "8pt" }}>
            <div
              style={{
                fontSize: "10pt",
                fontWeight: "bold",
                textTransform: "uppercase",
                borderBottom: "1pt solid #333",
                marginBottom: "3pt",
                color: "black",
                paddingBottom: "1pt",
              }}
            >
              WORK EXPERIENCE
            </div>
            <div>
              {sortedData.experience.map((exp, index) => (
                <div key={index} style={{ marginBottom: "8pt" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      marginBottom: "1pt",
                    }}
                  >
                    <div style={{ fontSize: "10pt", fontWeight: "bold", color: "black" }}>{exp.title}</div>
                    <div style={{ fontSize: "9pt", color: "#666" }}>
                      {formatDateToMMYYYY(exp.startDate)} - {formatDateToMMYYYY(exp.endDate) || "Present"}
                    </div>
                  </div>
                  <div style={{ fontSize: "9pt", color: "black", marginBottom: "3pt" }}>
                    {exp.company}
                    {exp.location && `, ${exp.location}`}
                  </div>
                  {exp.description && (
                    <div
                      style={{
                        fontSize: "9pt",
                        color: "black",
                        whiteSpace: "pre-line",
                        lineHeight: "1.3",
                        textAlign: "justify",
                      }}
                    >
                      {exp.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {sortedData?.education && sortedData.education.length > 0 && (
          <div style={{ marginBottom: "8pt" }}>
            <div
              style={{
                fontSize: "10pt",
                fontWeight: "bold",
                textTransform: "uppercase",
                borderBottom: "1pt solid #333",
                marginBottom: "3pt",
                color: "black",
                paddingBottom: "1pt",
              }}
            >
              EDUCATION
            </div>
            <div>
              {sortedData.education.map((edu, index) => (
                <div key={index} style={{ marginBottom: "6pt" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      marginBottom: "1pt",
                    }}
                  >
                    <div style={{ fontSize: "10pt", fontWeight: "bold", color: "black" }}>{edu.degree}</div>
                    <div style={{ fontSize: "9pt", color: "#666" }}>
                      {formatDateToMMYYYY(edu.startDate)} - {formatDateToMMYYYY(edu.endDate) || "End"}
                    </div>
                  </div>
                  <div style={{ fontSize: "9pt", color: "black", marginBottom: "2pt" }}>
                    {edu.school}
                    {edu.location && ` ${edu.location}`}
                  </div>
                  {edu.gpa && <div style={{ fontSize: "9pt", color: "#666" }}>GPA: {edu.gpa}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {sortedData?.skills && getSkillsArray(sortedData.skills).length > 0 && (
          <div style={{ marginBottom: "8pt" }}>
            <div
              style={{
                fontSize: "10pt",
                fontWeight: "bold",
                textTransform: "uppercase",
                borderBottom: "1pt solid #333",
                marginBottom: "3pt",
                color: "black",
                paddingBottom: "1pt",
              }}
            >
              TECHNICAL SKILLS
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "2pt 12pt",
                fontSize: "9pt",
                color: "black",
                lineHeight: "1.4",
              }}
            >
              {getSkillsArray(sortedData.skills).map((skill, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    marginBottom: "1pt",
                  }}
                >
                  <span style={{ marginRight: "4pt", flexShrink: 0 }}>•</span>
                  <span>{skill}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {sortedData?.projects && sortedData.projects.length > 0 && (
          <div style={{ marginBottom: "8pt" }}>
            <div
              style={{
                fontSize: "10pt",
                fontWeight: "bold",
                textTransform: "uppercase",
                borderBottom: "1pt solid #333",
                marginBottom: "3pt",
                color: "black",
                paddingBottom: "1pt",
              }}
            >
              PROJECTS
            </div>
            <div>
              {sortedData.projects.map((project, index) => (
                <div key={index} style={{ marginBottom: "6pt" }}>
                  <div style={{ fontSize: "10pt", fontWeight: "bold", color: "black", marginBottom: "2pt" }}>
                    {project.name || project.title}
                  </div>
                  {project.description && (
                    <div
                      style={{
                        fontSize: "9pt",
                        color: "black",
                        whiteSpace: "pre-line",
                        lineHeight: "1.3",
                        textAlign: "justify",
                      }}
                    >
                      {project.description.split("\n").map((line, lineIndex) =>
                        line.trim().startsWith("•") ? (
                          <div key={lineIndex} style={{ marginBottom: "2pt" }}>
                            {line}
                          </div>
                        ) : (
                          <div key={lineIndex} style={{ marginBottom: "2pt" }}>
                            • {line}
                          </div>
                        )
                      )}
                    </div>
                  )}
                  {project.technologies && project.technologies.length > 0 && (
                    <div style={{ fontSize: "8pt", color: "#666", fontStyle: "italic", marginTop: "2pt" }}>
                      Technologies: {project.technologies.join(", ")}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {sortedData?.certifications && sortedData.certifications.length > 0 && (
          <div style={{ marginBottom: "10pt" }}>
            <div
              style={{
                fontSize: "10pt",
                fontWeight: "bold",
                textTransform: "uppercase",
                borderBottom: "1pt solid #333",
                marginBottom: "4pt",
                color: "black",
              }}
            >
              CERTIFICATIONS
            </div>
            <div>
              {sortedData.certifications.map((cert, index) => (
                <div key={index} style={{ marginBottom: "4pt" }}>
                  <div style={{ fontSize: "9pt", fontWeight: "bold", color: "black" }}>{cert.name}</div>
                  {cert.issuer && (
                    <div style={{ fontSize: "8pt", color: "#666" }}>
                      {cert.issuer} • {cert.date}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );

    const renderModernTemplate = () => (
      <div
        className="printable-resume modern"
        style={{
          fontFamily: "Arial, sans-serif",
          backgroundColor: "white",
          fontSize: "10pt",
          lineHeight: "1.3",
          color: "black",
          overflow: "hidden",
        }}
      >
        {/* Blue header matching preview design */}
        <div
          style={{
            backgroundColor: "#2563eb",
            color: "white",
            padding: "12pt",
            marginBottom: "8pt",
            WebkitPrintColorAdjust: "exact",
            colorAdjust: "exact",
          }}
        >
          <h1
            style={{
              fontSize: "14pt",
              fontWeight: "bold",
              margin: "0 0 4pt 0",
              color: "white",
            }}
          >
            {sortedData?.personalInfo?.name || "Your Name"}
          </h1>
          <div
            style={{
              fontSize: "8pt",
              opacity: "0.9",
              margin: "0",
              color: "white",
            }}
          >
            {[
              sortedData?.personalInfo?.email,
              sortedData?.personalInfo?.phone,
              sortedData?.personalInfo?.location,
              sortedData?.personalInfo?.linkedIn,
              sortedData?.personalInfo?.github,
            ]
              .filter(Boolean)
              .join(" | ")}
          </div>
        </div>

        {/* Grid layout matching preview design */}
        <div
          style={{
            padding: "0 12pt",
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: "8pt",
          }}
        >
          {/* Left sidebar */}
          <div>
            {/* Skills section */}
            {sortedData?.skills && getSkillsArray(sortedData.skills).length > 0 && (
              <div style={{ marginBottom: "8pt" }}>
                <div
                  style={{
                    fontSize: "9pt",
                    color: "#2563eb",
                    fontWeight: "bold",
                    borderLeft: "2pt solid #2563eb",
                    paddingLeft: "6pt",
                    marginBottom: "4pt",
                    WebkitPrintColorAdjust: "exact",
                    colorAdjust: "exact",
                  }}
                >
                  Skills
                </div>
                {getSkillsArray(sortedData.skills).map((skill, index) => (
                  <div
                    key={index}
                    style={{
                      backgroundColor: "#f1f5f9",
                      padding: "3pt 6pt",
                      borderRadius: "8pt",
                      fontSize: "6pt",
                      textAlign: "center",
                      marginBottom: "3pt",
                      color: "black",
                      WebkitPrintColorAdjust: "exact",
                      colorAdjust: "exact",
                    }}
                  >
                    {skill}
                  </div>
                ))}
              </div>
            )}

            {/* Education in sidebar */}
            {sortedData?.education && sortedData.education.length > 0 && (
              <div style={{ marginBottom: "8pt" }}>
                <div
                  style={{
                    fontSize: "9pt",
                    color: "#2563eb",
                    fontWeight: "bold",
                    borderLeft: "2pt solid #2563eb",
                    paddingLeft: "6pt",
                    marginBottom: "4pt",
                    WebkitPrintColorAdjust: "exact",
                    colorAdjust: "exact",
                  }}
                >
                  Education
                </div>
                {sortedData.education.map((edu, index) => (
                  <div key={index} style={{ marginBottom: "4pt" }}>
                    <div style={{ fontSize: "7pt", fontWeight: "bold", color: "#374151" }}>{edu.degree}</div>
                    <div style={{ fontSize: "6pt", color: "#6b7280" }}>
                      {edu.school}
                      {edu.location && `, ${edu.location}`}
                    </div>
                    <div style={{ fontSize: "6pt", color: "#6b7280" }}>
                      {formatDateToMMYYYY(edu.startDate)} - {formatDateToMMYYYY(edu.endDate) || "Present"}
                    </div>
                    {edu.gpa && <div style={{ fontSize: "6pt", color: "#6b7280" }}>GPA: {edu.gpa}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right main content */}
          <div>
            {/* Summary */}
            {sortedData?.summary && (
              <div style={{ marginBottom: "8pt" }}>
                <div
                  style={{
                    fontSize: "9pt",
                    color: "#2563eb",
                    fontWeight: "bold",
                    borderLeft: "2pt solid #2563eb",
                    paddingLeft: "6pt",
                    marginBottom: "4pt",
                    WebkitPrintColorAdjust: "exact",
                    colorAdjust: "exact",
                  }}
                >
                  Summary
                </div>
                <div style={{ fontSize: "8pt", color: "#374151" }}>
                  {typeof sortedData.summary === "string" ? sortedData.summary : sortedData.summary?.content || ""}
                </div>
              </div>
            )}

            {/* Experience */}
            {sortedData?.experience && sortedData.experience.length > 0 && (
              <div style={{ marginBottom: "8pt" }}>
                <div
                  style={{
                    fontSize: "9pt",
                    color: "#2563eb",
                    fontWeight: "bold",
                    borderLeft: "2pt solid #2563eb",
                    paddingLeft: "6pt",
                    marginBottom: "4pt",
                    WebkitPrintColorAdjust: "exact",
                    colorAdjust: "exact",
                  }}
                >
                  Experience
                </div>
                {sortedData.experience.map((exp, index) => (
                  <div key={index} style={{ marginBottom: "6pt" }}>
                    <div style={{ fontSize: "8pt", fontWeight: "bold", color: "#374151" }}>{exp.title}</div>
                    <div style={{ fontSize: "7pt", color: "#6b7280", marginBottom: "3pt" }}>
                      {exp.company}
                      {exp.location && ` • ${exp.location}`}
                    </div>
                    <div style={{ fontSize: "7pt", color: "#6b7280", marginBottom: "3pt" }}>
                      {formatDateToMMYYYY(exp.startDate)} - {formatDateToMMYYYY(exp.endDate) || "Present"}
                    </div>
                    {exp.description && (
                      <div style={{ fontSize: "7pt", color: "#374151", whiteSpace: "pre-line" }}>
                        {exp.description}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Projects */}
            {sortedData?.projects && sortedData.projects.length > 0 && (
              <div style={{ marginBottom: "8pt" }}>
                <div
                  style={{
                    fontSize: "7pt",
                    color: "#2563eb",
                    fontWeight: "bold",
                    borderLeft: "2pt solid #2563eb",
                    paddingLeft: "4pt",
                    marginBottom: "2pt",
                    WebkitPrintColorAdjust: "exact",
                    colorAdjust: "exact",
                  }}
                >
                  Projects
                </div>
                {sortedData.projects.slice(0, 2).map((project, index) => (
                  <div key={index} style={{ marginBottom: "3pt" }}>
                    <div style={{ fontSize: "6pt", color: "#374151", fontWeight: "bold" }}>
                      {project.name || project.title}
                    </div>
                    {project.description && (
                      <div style={{ fontSize: "5pt", color: "#6b7280", marginTop: "1pt" }}>
                        {project.description.length > 50
                          ? project.description.substring(0, 50) + "..."
                          : project.description}
                      </div>
                    )}
                  </div>
                ))}
                {sortedData.projects.length > 2 && (
                  <div style={{ fontSize: "5pt", color: "#6b7280", marginTop: "1pt" }}>
                    + {sortedData.projects.length - 2} more projects
                  </div>
                )}
              </div>
            )}

            {/* Certifications */}
            {sortedData?.certifications && sortedData.certifications.length > 0 && (
              <div>
                <div
                  style={{
                    fontSize: "7pt",
                    color: "#2563eb",
                    fontWeight: "bold",
                    borderLeft: "2pt solid #2563eb",
                    paddingLeft: "4pt",
                    marginBottom: "2pt",
                    WebkitPrintColorAdjust: "exact",
                    colorAdjust: "exact",
                  }}
                >
                  Certifications
                </div>
                {sortedData.certifications.slice(0, 2).map((cert, index) => (
                  <div key={index} style={{ marginBottom: "3pt" }}>
                    <div style={{ fontSize: "6pt", color: "#374151", fontWeight: "bold" }}>{cert.name}</div>
                    {cert.issuer && (
                      <div style={{ fontSize: "5pt", color: "#6b7280" }}>
                        {cert.issuer} • {formatDateToMMYYYY(cert.date)}
                      </div>
                    )}
                  </div>
                ))}
                {sortedData.certifications.length > 2 && (
                  <div style={{ fontSize: "5pt", color: "#6b7280", marginTop: "1pt" }}>
                    + {sortedData.certifications.length - 2} more certs
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );

    const renderCreativeTemplate = () => (
      <div
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          padding: "6pt",
          borderRadius: "4pt",
          position: "relative",
          overflow: "hidden",
          WebkitPrintColorAdjust: "exact",
          colorAdjust: "exact",
        }}
      >
        <div
          className="printable-resume creative"
          style={{
            backgroundColor: "rgba(255,255,255,0.95)",
            borderRadius: "8pt",
            padding: "8pt",
            fontFamily: "Georgia, serif",
            fontSize: "8pt",
            lineHeight: "1.2",
            color: "black",
            WebkitPrintColorAdjust: "exact",
            colorAdjust: "exact",
          }}
        >
          {/* Header with circular profile and gradient text */}
          <div style={{ textAlign: "center", marginBottom: "6pt" }}>
            <div
              style={{
                width: "20pt",
                height: "20pt",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                margin: "0 auto 4pt auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "8pt",
                fontWeight: "bold",
                WebkitPrintColorAdjust: "exact",
                colorAdjust: "exact",
              }}
            >
              {(sortedData?.personalInfo?.name || "Y").charAt(0).toUpperCase()}
            </div>
            <div
              style={{
                fontSize: "8pt",
                fontWeight: "300",
                color: "#667eea", // Fallback color for gradient text
                marginBottom: "2pt",
              }}
            >
              {sortedData?.personalInfo?.name || "Your Name"}
            </div>
            <div
              style={{
                fontSize: "5pt",
                color: "#666",
                fontStyle: "italic",
                margin: "0",
              }}
            >
              {[
                sortedData?.personalInfo?.email,
                sortedData?.personalInfo?.phone,
                sortedData?.personalInfo?.location,
                sortedData?.personalInfo?.linkedIn,
                sortedData?.personalInfo?.github,
              ]
                .filter(Boolean)
                .join(" | ")}
            </div>
          </div>

          {/* Summary */}
          {sortedData?.summary && (
            <div style={{ marginBottom: "6pt" }}>
              <div
                style={{
                  fontSize: "6pt",
                  fontWeight: "bold",
                  color: "#667eea",
                  textTransform: "uppercase",
                  borderBottom: "0.5pt solid #667eea",
                  paddingBottom: "1pt",
                  marginBottom: "3pt",
                  WebkitPrintColorAdjust: "exact",
                  colorAdjust: "exact",
                }}
              >
                Summary
              </div>
              <div style={{ fontSize: "6pt", color: "black", textAlign: "justify" }}>
                {(() => {
                  const summaryText =
                    typeof sortedData.summary === "string"
                      ? sortedData.summary
                      : sortedData.summary?.content || "";
                  return summaryText.length > 80 ? summaryText.substring(0, 80) + "..." : summaryText;
                })()}
              </div>
            </div>
          )}

          {/* Experience */}
          {sortedData?.experience && sortedData.experience.length > 0 && (
            <div style={{ marginBottom: "6pt" }}>
              <div
                style={{
                  fontSize: "6pt",
                  fontWeight: "bold",
                  color: "#667eea",
                  textTransform: "uppercase",
                  borderBottom: "0.5pt solid #667eea",
                  paddingBottom: "1pt",
                  marginBottom: "3pt",
                  WebkitPrintColorAdjust: "exact",
                  colorAdjust: "exact",
                }}
              >
                Experience
              </div>
              {sortedData.experience.slice(0, 2).map((exp, index) => (
                <div key={index} style={{ marginBottom: "4pt" }}>
                  <div style={{ fontSize: "6pt", fontWeight: "bold", color: "black" }}>{exp.title}</div>
                  <div style={{ fontSize: "5pt", color: "#666", marginBottom: "1pt" }}>
                    {exp.company}
                    {exp.location && ` • ${exp.location}`}
                  </div>
                  <div style={{ fontSize: "5pt", color: "#667eea", fontStyle: "italic" }}>
                    {exp.startDate} - {exp.endDate || "Present"}
                  </div>
                  {exp.description && (
                    <div style={{ fontSize: "5pt", color: "black", marginTop: "1pt", textAlign: "justify" }}>
                      {exp.description.length > 60 ? exp.description.substring(0, 60) + "..." : exp.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Skills */}
          {sortedData?.skills && getSkillsArray(sortedData.skills).length > 0 && (
            <div style={{ marginBottom: "6pt" }}>
              <div
                style={{
                  fontSize: "6pt",
                  fontWeight: "bold",
                  color: "#667eea",
                  textTransform: "uppercase",
                  borderBottom: "0.5pt solid #667eea",
                  paddingBottom: "1pt",
                  marginBottom: "3pt",
                  WebkitPrintColorAdjust: "exact",
                  colorAdjust: "exact",
                }}
              >
                Skills
              </div>
              <div style={{ display: "flex", gap: "2pt", flexWrap: "wrap" }}>
                {getSkillsArray(sortedData.skills)
                  .slice(0, 6)
                  .map((skill, index) => (
                    <span
                      key={index}
                      style={{
                        backgroundColor: "rgba(102, 126, 234, 0.1)",
                        color: "#667eea",
                        padding: "1pt 3pt",
                        borderRadius: "6pt",
                        fontSize: "5pt",
                        border: "0.5pt solid #667eea",
                        WebkitPrintColorAdjust: "exact",
                        colorAdjust: "exact",
                      }}
                    >
                      {skill}
                    </span>
                  ))}
                {getSkillsArray(sortedData.skills).length > 6 && (
                  <span style={{ fontSize: "5pt", color: "#666", alignSelf: "center" }}>
                    +{getSkillsArray(sortedData.skills).length - 6}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Education */}
          {sortedData?.education && sortedData.education.length > 0 && (
            <div style={{ marginBottom: "6pt" }}>
              <div
                style={{
                  fontSize: "6pt",
                  fontWeight: "bold",
                  color: "#667eea",
                  textTransform: "uppercase",
                  borderBottom: "0.5pt solid #667eea",
                  paddingBottom: "1pt",
                  marginBottom: "3pt",
                  WebkitPrintColorAdjust: "exact",
                  colorAdjust: "exact",
                }}
              >
                Education
              </div>
              {sortedData.education.slice(0, 1).map((edu, index) => (
                <div key={index} style={{ marginBottom: "3pt" }}>
                  <div style={{ fontSize: "6pt", fontWeight: "bold", color: "black" }}>{edu.degree}</div>
                  <div style={{ fontSize: "5pt", color: "#666", marginBottom: "1pt" }}>
                    {edu.school}
                    {edu.location && `, ${edu.location}`}
                  </div>
                  <div style={{ fontSize: "5pt", color: "#667eea", fontStyle: "italic" }}>
                    {edu.startDate} - {edu.endDate || "Present"}
                  </div>
                  {edu.gpa && <div style={{ fontSize: "5pt", color: "#666" }}>GPA: {edu.gpa}</div>}
                </div>
              ))}
            </div>
          )}

          {/* Projects */}
          {sortedData?.projects && sortedData.projects.length > 0 && (
            <div style={{ marginBottom: "6pt" }}>
              <div
                style={{
                  fontSize: "6pt",
                  fontWeight: "bold",
                  color: "#667eea",
                  textTransform: "uppercase",
                  borderBottom: "0.5pt solid #667eea",
                  paddingBottom: "1pt",
                  marginBottom: "3pt",
                  WebkitPrintColorAdjust: "exact",
                  colorAdjust: "exact",
                }}
              >
                Projects
              </div>
              {sortedData.projects.slice(0, 2).map((project, index) => (
                <div key={index} style={{ marginBottom: "3pt" }}>
                  <div style={{ fontSize: "6pt", fontWeight: "bold", color: "black" }}>
                    {project.name || project.title}
                  </div>
                  {project.description && (
                    <div style={{ fontSize: "5pt", color: "black", marginTop: "1pt" }}>
                      {project.description.length > 40
                        ? project.description.substring(0, 40) + "..."
                        : project.description}
                    </div>
                  )}
                  {project.technologies && project.technologies.length > 0 && (
                    <div style={{ fontSize: "4pt", color: "#666", fontStyle: "italic", marginTop: "1pt" }}>
                      Tech: {project.technologies.slice(0, 3).join(", ")}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Certifications */}
          {sortedData?.certifications && sortedData.certifications.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: "6pt",
                  fontWeight: "bold",
                  color: "#667eea",
                  textTransform: "uppercase",
                  borderBottom: "0.5pt solid #667eea",
                  paddingBottom: "1pt",
                  marginBottom: "3pt",
                  WebkitPrintColorAdjust: "exact",
                  colorAdjust: "exact",
                }}
              >
                Certifications
              </div>
              {sortedData.certifications.slice(0, 2).map((cert, index) => (
                <div key={index} style={{ marginBottom: "2pt" }}>
                  <div style={{ fontSize: "6pt", fontWeight: "bold", color: "black" }}>{cert.name}</div>
                  {cert.issuer && (
                    <div style={{ fontSize: "5pt", color: "#666" }}>
                      {cert.issuer} • {cert.date}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );

    const renderTemplate = () => {
      switch (selectedTemplate.id) {
        case "classic":
          return renderClassicTemplate();
        case "modern":
          return renderFlexibleTemplate("modern");
        case "creative":
          return renderFlexibleTemplate("creative");
        default:
          return renderClassicTemplate();
      }
    };

    // Generate custom CSS for dynamic styling
    const customCSS = `
      <style>
        .printable-resume {
          font-size: ${fontSize}px !important;
          line-height: 1.4 !important;
        }
        
        .printable-resume .section {
          margin-bottom: ${sectionSpacing}px !important;
        }
        
        .printable-resume .section-title {
          color: ${titleColor} !important;
          font-size: ${fontSize * 1.1}px !important;
        }
        
        .printable-resume .section-content {
          font-size: ${fontSize * 0.9}px !important;
        }
        
        .printable-resume .experience-title,
        .printable-resume .education-title {
          color: ${subtitleColor} !important;
          font-size: ${fontSize * 0.95}px !important;
        }
        
        .printable-resume .experience-company,
        .printable-resume .education-school,
        .printable-resume .experience-date,
        .printable-resume .education-date {
          color: ${subtitleColor} !important;
          font-size: ${fontSize * 0.85}px !important;
        }
        
        .printable-resume .header h1 {
          font-size: ${fontSize * 1.4}px !important;
        }
        
        .printable-resume .header .contact {
          font-size: ${fontSize * 0.85}px !important;
        }
        
        .printable-resume .experience-description {
          font-size: ${fontSize * 0.8}px !important;
        }
        
        /* Modern template overrides */
        .printable-resume.modern .section-title {
          color: ${titleColor} !important;
          border-left-color: ${titleColor} !important;
        }
        
        .printable-resume.modern .modern-header {
          background-color: ${titleColor} !important;
        }
        
        /* Creative template overrides */
        .printable-resume.creative .section-title {
          color: ${titleColor} !important;
          border-bottom-color: ${titleColor} !important;
        }
        
        .printable-resume.creative .creative-header {
          background: linear-gradient(135deg, ${titleColor} 0%, ${subtitleColor} 100%) !important;
        }
        
        /* Classic template overrides */
        .printable-resume.classic .section-title {
          color: ${titleColor} !important;
        }
        
        .printable-resume.classic .header {
          border-bottom-color: ${titleColor} !important;
        }
      </style>
    `;

    return (
      <div className="print-preview">
        <div dangerouslySetInnerHTML={{ __html: printStyles }} />
        <div dangerouslySetInnerHTML={{ __html: customCSS }} />
        {showPrintButton && (
          <div className="no-print" style={{ textAlign: "center", marginBottom: "20px" }}>
            <button
              onClick={handlePrint}
              style={{
                backgroundColor: "#2563eb",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#1d4ed8")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "#2563eb")}
            >
              📥 Download Resume
            </button>
            <div
              style={{
                marginTop: "10px",
                fontSize: "12px",
                color: "#666",
                backgroundColor: "#f8f9fa",
                padding: "8px 12px",
                borderRadius: "4px",
                border: "1px solid #e9ecef",
              }}
            >
              💡 <strong>Print Tip:</strong> Choose "Save as PDF" in your browser's print dialog
            </div>
          </div>
        )}
        {renderTemplate()}
      </div>
    );
  }
);

export default PrintableResume;
